import React, { TextareaHTMLAttributes, forwardRef, useRef } from 'react';
import { useAutoResizeTextarea } from '../../hooks/useAutoResizeTextarea';

interface AutoResizeTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  minRows?: number;
  maxRows?: number;
}

const AutoResizeTextarea = forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  (
    {
      label,
      error,
      helperText,
      className = '',
      minRows = 2,
      maxRows = 20,
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    const { adjustHeight } = useAutoResizeTextarea(textareaRef, String(value ?? ''), {
      minRows,
      maxRows,
    });

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
      // Call adjustHeight immediately for responsive feel (before state updates)
      adjustHeight();
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {label}
          </label>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          className={`
            w-full px-4 py-3 rounded-xl
            bg-white dark:bg-gray-800
            border-2 ${error ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}
            hover:border-primary-400 dark:hover:border-primary-600
            focus:outline-none focus:ring-4 ${error ? 'focus:ring-red-500/20' : 'focus:ring-primary-500/20'} focus:border-primary-500 dark:focus:border-primary-400
            text-gray-900 dark:text-gray-100 font-medium
            placeholder:text-gray-400 dark:placeholder:text-gray-500
            transition-all duration-300
            shadow-sm hover:shadow-md
            resize-none
            ${className}
          `}
          {...props}
        />
        {error && (
          <p className="mt-2 text-sm text-red-500 font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
      </div>
    );
  }
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';

export default AutoResizeTextarea;
